// ---------------------------------------------------------------------------
// Premium table types, the bundled fallback data, and the quote calculation.
//
// The live table is served from Cloudflare KV via functions/api/premiums.ts.
// FALLBACK_TABLE mirrors the 2026 spreadsheet and is compiled into both the
// app (so the calculator works if the fetch fails) and the Pages Function (so
// /api/premiums still responds before any KV write). Keep it in sync with
// data/premiums.csv — scripts/csv-to-kv.mjs produces the same JSON shape.
// ---------------------------------------------------------------------------

export type Band = "under65" | "over65";
export type Column = "individual" | "family";
export type FamilySize = 1 | 2;

export interface PlanRates {
  individual: Record<Band, number>;
  family: Record<Band, number>;
}

export interface Plan {
  id: string;
  name: string;
  /** Highest age at which someone may take up this plan. */
  maxEntryAge: number;
  rates: PlanRates;
}

export interface PremiumTable {
  /** Effective date / identifier of this rate set, e.g. "2026-05-01". */
  version: string;
  currency: string;
  period: string;
  minEntryAge: number;
  plans: Plan[];
}

/** Bundled 2026 rates (effective 1 May 2026) — fallback + build-time default. */
export const FALLBACK_TABLE: PremiumTable = {
  version: "2026-05-01",
  currency: "R",
  period: "monthly",
  minEntryAge: 18,
  plans: [
    {
      id: "gap200",
      name: "Gap 200",
      maxEntryAge: 65,
      rates: {
        individual: { under65: 136, over65: 382 },
        family: { under65: 149, over65: 414 },
      },
    },
    {
      id: "gap500",
      name: "Gap 500",
      maxEntryAge: 70,
      rates: {
        individual: { under65: 285, over65: 629 },
        family: { under65: 310, over65: 684 },
      },
    },
    {
      id: "combined200",
      name: "Combined 200",
      maxEntryAge: 65,
      rates: {
        individual: { under65: 210, over65: 460 },
        family: { under65: 232, over65: 497 },
      },
    },
    {
      id: "combined400",
      name: "Combined 400",
      maxEntryAge: 65,
      rates: {
        individual: { under65: 365, over65: 647 },
        family: { under65: 390, over65: 777 },
      },
    },
    {
      id: "combined500",
      name: "Combined 500",
      maxEntryAge: 65,
      rates: {
        individual: { under65: 485, over65: 927 },
        family: { under65: 540, over65: 1002 },
      },
    },
    {
      id: "ultra",
      name: "Ultra",
      maxEntryAge: 65,
      rates: {
        individual: { under65: 680, over65: 1245 },
        family: { under65: 755, over65: 1351 },
      },
    },
  ],
};

export type QuoteResult =
  | { status: "ok"; premium: number; band: Band; column: Column }
  | { status: "over-max"; maxEntryAge: number }
  | { status: "below-min"; minEntryAge: number }
  | { status: "unknown-plan" };

/**
 * Resolve the monthly premium for the given plan, family size and age.
 * Rate break is at age 65 (>= 65 uses the "over65" band). If the age exceeds
 * the plan's maximum entry age, no premium is returned — the caller shows the
 * "contact us" message instead.
 */
export function calcPremium(
  table: PremiumTable,
  planId: string,
  familySize: FamilySize,
  age: number,
): QuoteResult {
  const plan = table.plans.find((p) => p.id === planId);
  if (!plan) return { status: "unknown-plan" };
  if (age < table.minEntryAge) {
    return { status: "below-min", minEntryAge: table.minEntryAge };
  }
  if (age > plan.maxEntryAge) {
    return { status: "over-max", maxEntryAge: plan.maxEntryAge };
  }
  const band: Band = age >= 65 ? "over65" : "under65";
  const column: Column = familySize === 1 ? "individual" : "family";
  return { status: "ok", premium: plan.rates[column][band], band, column };
}

/** Basic shape guard for data coming back from the API before we trust it. */
export function isPremiumTable(value: unknown): value is PremiumTable {
  if (typeof value !== "object" || value === null) return false;
  const t = value as Partial<PremiumTable>;
  return (
    typeof t.version === "string" &&
    typeof t.minEntryAge === "number" &&
    Array.isArray(t.plans) &&
    t.plans.every(
      (p) =>
        typeof p?.id === "string" &&
        typeof p?.name === "string" &&
        typeof p?.maxEntryAge === "number" &&
        !!p?.rates?.individual &&
        !!p?.rates?.family,
    )
  );
}
